import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

export default function TypographyBlock() {
  return (
    <Section tone="default" density="default" id="typography">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">Typography</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            One typeface.
            <br />
            One personality.
          </Heading>
          <Text size="xl" tone="secondary" className="mt-6">
            Monofor uses TT Hoves across every surface: wordmark, website, and
            collateral. It is a precise, modern sans-serif with a clear
            geometric backbone that reads cleanly at every size.
          </Text>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="rounded-3xl border border-line-subtle bg-surface-subtle p-10 lg:p-14 shadow-card">
            <div className="text-eyebrow font-semibold uppercase text-ink-tertiary">
              TT Hoves
            </div>
            <div className="mt-5 text-display-2xl font-medium text-ink-primary leading-[1.05] tracking-tighter">
              Aa Bb Cc
            </div>
            <div className="mt-3 text-body-md text-ink-secondary tabular-nums tracking-[-0.01em]">
              0 1 2 3 4 5 6 7 8 9
            </div>
            <div className="mt-8 grid gap-3">
              <SpecimenLine weight="font-normal" label="Regular" />
              <SpecimenLine weight="font-medium" label="Medium" />
              <SpecimenLine weight="font-semibold" label="Semibold" />
              <SpecimenLine weight="font-bold" label="Bold" />
            </div>
          </div>

          <div className="rounded-3xl border border-line-subtle bg-surface p-8 lg:p-10 shadow-card">
            <Eyebrow tone="muted">Rules of thumb</Eyebrow>
            <ul className="mt-6 space-y-5 text-body-md text-ink-secondary">
              <li className="flex gap-3">
                <Dot />
                <span>
                  Headlines and display use{' '}
                  <span className="font-medium text-ink-primary">Medium 500</span>{' '}
                  with tight tracking.
                </span>
              </li>
              <li className="flex gap-3">
                <Dot />
                <span>
                  Body copy is{' '}
                  <span className="font-medium text-ink-primary">Regular 400</span>{' '}
                  at 1.6 line-height.
                </span>
              </li>
              <li className="flex gap-3">
                <Dot />
                <span>
                  Eyebrows and small labels are{' '}
                  <span className="font-medium text-ink-primary">Semibold 600</span>{' '}
                  uppercase with 0.14em tracking.
                </span>
              </li>
              <li className="flex gap-3">
                <Dot />
                <span>
                  For data and prices, use tabular figures so columns align cleanly.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </Container>
    </Section>
  )
}

function SpecimenLine({
  weight,
  label,
}: {
  weight: string
  label: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line-subtle pb-3 last:border-b-0">
      <span className={`text-display-md text-ink-primary ${weight} tracking-tight`}>
        Empowered identity
      </span>
      <span className="text-caption uppercase tracking-[0.14em] text-ink-tertiary">
        {label}
      </span>
    </div>
  )
}

function Dot() {
  return (
    <span className="mt-2 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-600" />
  )
}
